/**
 * accountMember.component.tsx
 * Sentriqs
 *
 * Created by: Dylan Knight
 * Copyright (c) 2024 Sentriqs, Inc. All rights reserved.
 *
**/

import React from "react";
import { useNavigate, useParams, Link } from "react-router-dom";

import MenuIcon from "@mui/icons-material/Menu";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { Button, Breadcrumbs, CircularProgress, IconButton } from "@mui/material"

import { AuthContext } from "../auth/auth.context";
import { AccountNavContext } from "./account.context";
import { AccountMemberDetailsComponent } from "./accountMemberDetail.component";
import { AccountMemberGroupsComponent } from "./accountMemberGroups.component";
import { ErrorSnackBar } from "../components/errorSnackBar.component";
import { SCAvatar } from "../components/avatar.component";

import { Device, Membership } from "../models/auth";

import { authServiceFactory } from "../services/auth.service";

interface ContainerRouteParams {
  userID: string,
  [key: string]: string | undefined
}
export function AccountMemberContainer(): React.ReactElement<any, any> {
  const authContext = React.useContext<AuthContext>(AuthContext);
  const acctContext = React.useContext<AccountNavContext>(AccountNavContext);
  const { userID } = useParams<ContainerRouteParams>() as {userID: string};
  const [errMsg, setErrMsg] = React.useState("");
  const [member, setMember] = React.useState<Membership | null>(null);
  const [devices, setDevices] = React.useState<Array<Device>>([]);
  const navigate = useNavigate();

  React.useEffect(function () {
    const accountID = authContext.membership?.accountID;
    if (!accountID) { return; }
    const authService = authServiceFactory();
    authService.memberFor(userID).then(setMember, err => {
      console.error("error getting membership:", err);
      setErrMsg(err.message);
    });
    authServiceFactory().getMemberDevices(userID).then(setDevices, err => {
      console.error("error fetching devices:", err);
      setErrMsg(err.message);
    });
  }, [userID]);

  function updateMembership(update: Partial<Membership>): Promise<Membership> | undefined {  
    try {
      return authServiceFactory().updateMembership(userID, update);
    } catch (err) {
      console.error("Error updating membership:", err);
      setErrMsg((err as Error).message);
    }
  }

  async function deleteMembership(member: Membership | null): Promise<void> {
    if (!member) { return Promise.resolve(); }
    try {
      authServiceFactory().deleteMembership(member.userID);
      navigate(-1);
    } catch (err) {
      console.error("Error deleting member:", err);
      setErrMsg((err as Error).message);
    }
  }

  const compAvailable = acctContext.currentAccount?.remainingFreeMemberships ?
      acctContext.currentAccount.remainingFreeMemberships > 0 : false
  return <div className="account-member-container">
    <header className="group-header-nav attached-left-nav">
      <IconButton className="left-nav-outer-toggle" size="small"
          disableRipple disableTouchRipple
          onClick={acctContext.toggleLeftNav}>
        <MenuIcon />
      </IconButton>
      <h3>Member: {member?.firstname || ""} {member?.lastname || ""}</h3>
    </header>

    <div className="breadcrumb-container">
      <Breadcrumbs aria-label="breadcrumb" className="main-breadcrumbs">
        <Link to="/admin/members/">
          <NavigateBeforeIcon fontSize="small" />
          <span>Members</span>
        </Link>
      </Breadcrumbs>
    </div>
    <AccountMemberComponent member={member} devices={devices}
      compAvailable={compAvailable}
      updateMember={updateMembership}
      deleteMember={deleteMembership}
      onError={err => setErrMsg(err.message)} />

    <ErrorSnackBar errMsg={errMsg} didClose={() => setErrMsg("") }/>
  </div>;
}

interface MemberProps {
  member: Membership | null,
  devices: Array<Device>,
  compAvailable: boolean,
  updateMember: (update: Partial<Membership>) => Promise<Membership> | undefined,
  deleteMember: (member: Membership | null) => Promise<void>,
  onError: (err: Error) => void
}

export function AccountMemberComponent(props: MemberProps): React.ReactElement<any, any> {
  const [deleting, setDeleting] = React.useState<boolean>(false);

  async function doDeleteMember(): Promise<void> {
    setDeleting(true);
    await props.deleteMember(props.member);
    setDeleting(false);
  }

  const editComp = props.member?.isComp || props.compAvailable;
  return props.member ? <>
    <article className="account-member-list-edit-item">
      <SCAvatar text={props.member?.avatarText || "XX"} url={props.member?.avatarUrl || ""} />
      <div className="form-wrapper">
        <AccountMemberDetailsComponent member={props.member} editComp={editComp} saveUpdate={props.updateMember} />
        <div className="button-row">
        {deleting ?
          <CircularProgress size={20} color="secondary" /> :
          <Button variant="contained" color="secondary" className="delete-btn"
              disableRipple disableTouchRipple
              onClick={doDeleteMember}>
            Delete
          </Button>}
        </div>
      </div>
    </article>
    <article className="account-info-block">
      <AccountMemberGroupsComponent member={props.member} devices={props.devices} onError={props.onError} />
    </article>
  </> : <></>;
}

